import React, { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import type { BikeWithLocation } from "../../types/bike";
import { useBikeStore } from "../../store/useBikeStore";
import { useVehicleCategoryStore } from "../../store/useVehicleCategoryStore";

interface BikeMultiSelectProps {
  selectedBikeIds: string[];
  onChange: (bikeIds: string[]) => void;
  disabled?: boolean;
  label?: string;
  required?: boolean;
}

export const BikeMultiSelect: React.FC<BikeMultiSelectProps> = ({
  selectedBikeIds,
  onChange,
  disabled = false,
  label = "Select Vehicles",
  required = false,
}) => {
  const { bikes, loadIfNeeded } = useBikeStore();
  const { getCategoryById } = useVehicleCategoryStore();
  const [bikeSearchQuery, setBikeSearchQuery] = useState("");

  useEffect(() => {
    loadIfNeeded().catch(console.error);
  }, [loadIfNeeded]);

  const bikePool = useMemo((): BikeWithLocation[] => {
    const missingIds = selectedBikeIds.filter(
      (id) => !bikes.some((b) => b.bike_id === id),
    );
    const placeholders: BikeWithLocation[] = missingIds.map((bike_id) => ({
      bike_id,
      status: "active",
      category_id: null,
      bike_sn: null,
      max_speed: null,
      comment: null,
      primary_city_id: null,
      fleet_id: null,
      ui_hidden: true,
      created_at: "",
      updated_at: "",
    }));
    return [...bikes, ...placeholders];
  }, [bikes, selectedBikeIds]);

  const filteredBikes = useMemo(() => {
    const query = bikeSearchQuery.trim().toLowerCase();
    if (!query) return bikePool;
    return bikePool.filter((bike) => {
      const idMatch = bike.bike_id.toLowerCase().includes(query);
      const snMatch = (bike.bike_sn ?? "").toLowerCase().includes(query);
      return idMatch || snMatch;
    });
  }, [bikePool, bikeSearchQuery]);

  const bikesForDisplay = useMemo(() => {
    const selected: BikeWithLocation[] = [];
    const unselected: BikeWithLocation[] = [];

    filteredBikes.forEach((bike) => {
      if (selectedBikeIds.includes(bike.bike_id)) {
        selected.push(bike);
      } else {
        unselected.push(bike);
      }
    });

    return [...selected, ...unselected];
  }, [filteredBikes, selectedBikeIds]);

  const handleBikeToggle = (bikeId: string) => {
    if (disabled) return;
    onChange(
      selectedBikeIds.includes(bikeId)
        ? selectedBikeIds.filter((id) => id !== bikeId)
        : [...selectedBikeIds, bikeId],
    );
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
        {required && <span className="text-red-500"> *</span>}
        <span className="text-xs text-gray-500 ml-2">
          ({selectedBikeIds.length} selected)
        </span>
      </label>
      <div className="relative mb-2">
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"
          aria-hidden="true"
        />
        <input
          type="text"
          value={bikeSearchQuery}
          onChange={(e) => setBikeSearchQuery(e.target.value)}
          placeholder="Search by bike ID or SN..."
          disabled={disabled}
          className="block w-full border border-gray-300 rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
          aria-label="Search vehicles by ID or serial number"
        />
      </div>
      <div className="border border-gray-300 rounded-md p-3 h-48 overflow-y-auto">
        {bikePool.length === 0 ? (
          <p className="text-sm text-gray-500">No vehicles available</p>
        ) : filteredBikes.length === 0 ? (
          <p className="text-sm text-gray-500">No vehicles match your search</p>
        ) : (
          <div className="space-y-2">
            {bikesForDisplay.map((bike) => (
              <label
                key={bike.bike_id}
                className={`flex items-center space-x-2 p-2 rounded ${
                  disabled
                    ? "cursor-not-allowed opacity-60"
                    : "cursor-pointer hover:bg-gray-50"
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedBikeIds.includes(bike.bike_id)}
                  onChange={() => handleBikeToggle(bike.bike_id)}
                  disabled={disabled}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-900">
                  {bike.bike_id}
                  {bike.ui_hidden ? (
                    <span className="text-amber-700 font-normal">
                      {" "}
                      (hidden)
                    </span>
                  ) : null}{" "}
                  - {getCategoryById(bike.category_id)}
                  {bike.fleet_id ? (
                    <span className="text-gray-500 font-normal">
                      {" "}
                      (fleet assigned)
                    </span>
                  ) : null}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
